<script lang="ts">
	import { onMount } from 'svelte';
	import { format } from 'svelte-i18n';
	import { Icon, SankeyChart, Text } from '@Shelly/shelly-design-system';
	import { devices, rooms } from 'src/stores';
	import { home_data, user_data } from 'src/stores/account/user';
	import {
		consumption_by_device_account,
		consumption_device,
		consumption_rooms,
	} from 'src/stores/features/consumption';
	import { mobile } from 'src/services/mobile-rework';
	import {
		createMemberSettingsSnapshot,
		createMemberStatusSnapshot,
	} from 'src/stores/device-runtime/status-selectors';
	import { getPVSetupDevices } from 'src/components/Consumption/PVSetupEnergy/pv_setup_helper';
	import { buildConsumptionStores } from 'src/components/EnergyMonitoring/helpers/consumption_helpers';
	import { energyMonitoringDataMode } from 'src/components/EnergyMonitoring/helpers/store';
	import { convertToValueAndUnit } from 'src/helpers/units_helpers';
	import { getDeviceImage } from 'src/helpers/device_state_view_helpers';
	import {
		applyEnergyFlowVisibility,
		buildDemoEnergyFlow,
		buildEnergyFlowSankey,
		COMPACT_FLOW_LIMITS,
		DEFAULT_FLOW_LIMITS,
		getEnergyChartEmptyMessage,
		type EnergyFlowSupplyMode,
		type EnergyFlowVisibility,
	} from 'src/components/EnergyMonitoring/helpers/energy_flow_helpers';
	import {
		getActivePvSetupType,
		getPvGenerationFlow,
	} from 'src/components/EnergyMonitoring/helpers/pv_flow_helpers';
	import EmWidgetState from '../EmWidgetState.svelte';
	import EmWidgetShell from '../EmWidgetShell.svelte';
	import EmChartLegend from '../EmChartLegend.svelte';
	import {
		ENERGY_ADD_DEVICE_HREF,
		hasPowerMeterDevices,
	} from '../helpers/energy_widget_state_helpers';

	export let embedded = false;
	export let initialVisibility: EnergyFlowVisibility | undefined;
	export let maxRoomNodes: number | undefined;
	export let maxDeviceNodes: number | undefined;
	export let staticPreview = false;
	export let supplyMode: EnergyFlowSupplyMode = 'auto';

	let viewportWidth = 1200;
	let containerWidth = 0;
	let visibility: EnergyFlowVisibility = initialVisibility ?? {
		showSupply: true,
		showRooms: true,
		showDevices: true,
	};

	onMount(() => {
		const syncWidth = () => {
			viewportWidth = window.innerWidth;
		};
		syncWidth();
		window.addEventListener('resize', syncWidth);
		return () => window.removeEventListener('resize', syncWidth);
	});

	const observeFlowShell = (node: HTMLDivElement) => {
		const syncContainerWidth = () => {
			containerWidth = node.clientWidth;
		};
		syncContainerWidth();

		const observer =
			typeof ResizeObserver !== 'undefined'
				? new ResizeObserver((entries) => {
						containerWidth = entries[0]?.contentRect.width ?? node.clientWidth;
					})
				: null;
		observer?.observe(node);

		return {
			destroy() {
				observer?.disconnect();
			},
		};
	};

	$: chartWidth = containerWidth || viewportWidth;
	$: isCompact = mobile.isMobile || chartWidth <= 760;
	$: flowLimits = {
		...(isCompact ? COMPACT_FLOW_LIMITS : DEFAULT_FLOW_LIMITS),
		...(maxRoomNodes != null ? { maxRooms: maxRoomNodes } : {}),
		...(maxDeviceNodes != null
			? {
					maxDevicesPerRoom: maxDeviceNodes,
					maxFlatDevices: maxDeviceNodes,
				}
			: {}),
	};
	$: nodeWidth = chartWidth < 420 ? 10 : isCompact ? 12 : 18;
	$: nodePadding = chartWidth < 420 ? 10 : isCompact ? 14 : 22;
	$: labelPadding = isCompact ? 14 : 24;
	$: compactLabelChars = chartWidth < 420 ? 12 : isCompact ? 18 : 24;

	$: consumptionStores = buildConsumptionStores({
		rooms: $rooms,
		devices: $devices,
		consumptionRooms: $consumption_rooms,
		consumptionDevice: $consumption_device,
		consumptionByDeviceAccount: $consumption_by_device_account,
	});

	$: pvSetupType = getActivePvSetupType();
	$: pvSetupDevices = $home_data
		? getPVSetupDevices(pvSetupType)
		: { gridDevices: [], solarDevices: [] };
	$: pvDeviceIds = [...pvSetupDevices.gridDevices, ...pvSetupDevices.solarDevices].map(
		(dev) => dev.id,
	);
	$: memberStatusStore = createMemberStatusSnapshot(pvDeviceIds);
	$: memberSettingsStore = createMemberSettingsSnapshot(pvDeviceIds);
	$: pvFlow =
		pvDeviceIds.length > 0
			? getPvGenerationFlow(
					pvSetupType,
					$memberStatusStore as Record<string, Record<string, unknown>>,
					$memberSettingsStore as Record<string, Record<string, unknown>>,
				)
			: null;

	$: liveSankeyData = buildEnergyFlowSankey(
		consumptionStores,
		$devices,
		$rooms,
		(key, fallback) => $format(key, { default: fallback }),
		visibility,
		flowLimits,
		pvFlow,
		supplyMode,
		$user_data?.available_units?.[String($user_data?.user_id ?? '')] ?? '',
	);

	$: demoSankeyData = buildDemoEnergyFlow(
		(key, fallback) => $format(key, { default: fallback }),
		supplyMode,
	);
	$: useDemoData = staticPreview || $energyMonitoringDataMode === 'demo';
	$: sankeyData = useDemoData ? applyEnergyFlowVisibility(demoSankeyData, visibility) : liveSankeyData;
	$: compactColumnLabelCount = sankeyData
		? Math.max(
				sankeyData.nodes.filter((node) =>
					['grid', 'generation', 'battery'].includes(node.role ?? ''),
				).length,
				sankeyData.nodes.filter((node) => node.role === 'room').length,
				sankeyData.nodes.filter((node) =>
					['device', 'other', 'export'].includes(node.role ?? ''),
				).length,
			)
		: 0;
	$: sankeyHeight = isCompact ? Math.max(520, compactColumnLabelCount * 34 + 128) : 360;
	$: sankeyKey =
		sankeyData?.links
			.map((link) => `${link.source}>${link.target}:${link.value}`)
			.sort()
			.join('|') ?? 'empty';
	// The Sankey layout mutates its input nodes and links; isolate it from the
	// semantic model used by the legend and visibility controls.
	$: chartSankeyData = sankeyData
		? {
				...sankeyData,
				nodes: sankeyData.nodes.map((node) => {
					const deviceId = node.identity?.startsWith('device:')
						? node.identity.slice('device:'.length)
						: '';
					const hasOutgoingFlow = sankeyData.links.some((link) => link.source === node.id);
					return {
						...node,
						image:
							deviceId && $devices[deviceId]
								? getDeviceImage($devices[deviceId])
								: undefined,
						showLabel: true,
						labelSide:
							['grid', 'generation', 'battery'].includes(node.role ?? '') ||
							(node.role === 'room' && hasOutgoingFlow)
								? ('before' as const)
								: ('after' as const),
					};
				}),
				links: sankeyData.links.map((link) => ({ ...link })),
			}
		: undefined;
	$: nodeLegendItems =
		sankeyData?.nodes.map((node) => {
			const incoming = sankeyData.links
				.filter((link) => link.target === node.id)
				.reduce((sum, link) => sum + link.value, 0);
			const outgoing = sankeyData.links
				.filter((link) => link.source === node.id)
				.reduce((sum, link) => sum + link.value, 0);
			const display = convertToValueAndUnit(Math.max(incoming, outgoing), sankeyData.unit);
			return {
				id: node.identity ?? node.id,
				label: node.id,
				color: node.color ?? 'var(--color-muted)',
				valueLabel: `${display.value} ${display.unit}`,
				role: node.role,
			};
		}) ?? [];
	$: emptyMessage = getEnergyChartEmptyMessage($devices, (key, fallback) =>
		$format(key, { default: fallback }),
	);
</script>

{#snippet sourceNote()}
	{#if sankeyData?.sourceNote}
		<div class="em-flow-source-note em-flow-source-note-{sankeyData.sourceNote.provenance}">
			<Icon
				icon={sankeyData.sourceNote.provenance === 'measured'
					? 'fas fa-gauge-high'
					: 'fas fa-calculator'}
				size="xs"
			/>
			<Text styleType="miniature">
				{$format(sankeyData.sourceNote.key, {
					default: sankeyData.sourceNote.default,
				})}
			</Text>
		</div>
	{/if}
{/snippet}

{#if embedded}
	<div class="em-flow-widget">
		<div class="em-flow-body">
			{#if sankeyData}
				<div class="em-flow-shell" use:observeFlowShell>
					{@render sourceNote()}
					<div class="em-sankey-shell" class:em-sankey-shell-compact={isCompact}>
						{#key sankeyKey}
							<SankeyChart
								data={chartSankeyData}
								height={sankeyHeight}
								{nodeWidth}
								{nodePadding}
								{labelPadding}
								terminalLabelsOnly={false}
								terminalLabelsOutside={false}
								showNodeImages={false}
								maxLabelChars={compactLabelChars}
							/>
						{/key}
					</div>
					<EmChartLegend
						items={nodeLegendItems}
						class={isCompact
							? 'em-flow-node-legend em-flow-node-legend-compact'
							: 'em-flow-node-legend'}
					/>
				</div>
			{:else}
				<EmWidgetState
					kind={hasPowerMeterDevices($devices) ? 'empty' : 'setup'}
					message={emptyMessage}
					minHeight="220px"
					actionHref={!hasPowerMeterDevices($devices) ? ENERGY_ADD_DEVICE_HREF : ''}
					actionLabel={!hasPowerMeterDevices($devices)
						? $format('button_add_device', { default: 'Add device' })
						: ''}
				/>
			{/if}
		</div>
	</div>
{:else}
	<div class="em-flow-widget-shell">
		<EmWidgetShell
			widgetClass="energy-widget-flow"
			title={$format('energy_monitoring_flow_title', { default: 'Energy flow' })}
		>
			{#if sankeyData}
				<div class="em-flow-shell" use:observeFlowShell>
					{@render sourceNote()}
					<div class="em-sankey-shell" class:em-sankey-shell-compact={isCompact}>
						{#key sankeyKey}
							<SankeyChart
								data={chartSankeyData}
								height={sankeyHeight}
								{nodeWidth}
								{nodePadding}
								{labelPadding}
								terminalLabelsOnly={false}
								terminalLabelsOutside={false}
								showNodeImages={false}
								maxLabelChars={compactLabelChars}
							/>
						{/key}
					</div>
					<EmChartLegend
						items={nodeLegendItems}
						class={isCompact
							? 'em-flow-node-legend em-flow-node-legend-compact'
							: 'em-flow-node-legend'}
					/>
				</div>
			{:else}
				<EmWidgetState
					kind={hasPowerMeterDevices($devices) ? 'empty' : 'setup'}
					message={emptyMessage}
					minHeight="280px"
					actionHref={!hasPowerMeterDevices($devices) ? ENERGY_ADD_DEVICE_HREF : ''}
					actionLabel={!hasPowerMeterDevices($devices)
						? $format('button_add_device', { default: 'Add device' })
						: ''}
				/>
			{/if}
		</EmWidgetShell>
	</div>
{/if}

<style>
	.em-flow-widget {
		display: flex;
		flex-direction: column;
		flex: 1;
		min-height: 0;
		min-width: 0;
	}

	.em-flow-widget-shell {
		display: contents;
	}

	.em-flow-widget-shell :global(.energy-widget-flow) {
		display: flex;
		flex-direction: column;
		flex: 1;
		min-height: 0;
		min-width: 0;
	}

	.em-flow-widget-shell :global(.energy-widget-flow .energy-widget-heading) {
		align-items: center;
		margin-bottom: var(--spacing-s);
	}

	.em-flow-body {
		display: flex;
		flex-direction: column;
		flex: 1;
		min-height: 0;
		min-width: 0;
	}

	.em-flow-shell {
		display: flex;
		flex-direction: column;
		flex: 1;
		min-width: 0;
		gap: var(--spacing-s);
	}

	.em-sankey-shell {
		display: flex;
		align-items: center;
		justify-content: center;
		flex: 1;
		min-height: 220px;
		min-width: 0;
		width: 100%;
		overflow: visible;
		padding: 0 var(--spacing-xs);
	}

	.em-flow-source-note {
		display: flex;
		align-items: center;
		align-self: center;
		gap: var(--spacing-xs);
		padding: var(--spacing-xs) var(--spacing-s);
		border-radius: var(--radius-pill);
		color: var(--color-muted);
		background: color-mix(in srgb, var(--color-2) 72%, transparent);
	}

	.em-flow-source-note-calculated {
		color: var(--color-warning);
	}

	.em-flow-shell :global(.lc-sankey),
	.em-flow-shell :global(.lc-sankey svg),
	.em-flow-shell :global(.layer-chart-theme) {
		overflow: visible;
	}

	.em-sankey-shell :global(.lc-sankey),
	.em-sankey-shell :global(.layer-chart-theme) {
		width: 100%;
	}

	.em-flow-shell :global(.em-flow-node-legend-compact) {
		display: grid;
		grid-template-columns: repeat(auto-fit, minmax(128px, max-content));
		justify-content: center;
		align-items: center;
		gap: var(--spacing-xs) var(--spacing-s);
		width: 100%;
		overflow: visible;
		padding-inline: var(--spacing-xs);
	}

	.em-flow-shell :global(.em-flow-node-legend-compact .em-chart-legend-item) {
		max-width: 160px;
	}

	.em-flow-shell :global(.em-flow-node-legend-compact .text-600) {
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
	}
</style>
